'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import Link from 'next/link';
import {
  Accordion,
  Box,
  Button,
  Container,
  Group,
  Paper,
  SimpleGrid,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import { IconArrowRight, IconChevronLeft, IconChevronRight } from '@tabler/icons-react';
import type { Locale } from '../../i18n/locales';
import { pickLocaleString } from '../../lib/content/localization';
import { getCarComparisonLinkProps, resolvePublicHref } from '../../lib/content/comparisonLinks';
import { stripHtmlToPlainText } from '../../lib/content/contentPagePresentation';
import { Header } from '../layout/Header';
import { Footer } from '../layout/Footer';
import { PublicPageShell } from '../layout/PublicPageShell';
import type { HomepageData } from '../../types/content';
import { HeroBlock } from './blocks/HeroBlock';
import { StatsGridBlock } from './blocks/StatsGridBlock';
import { useRealtimeContent } from '../../hooks/useRealtimeContent';
import { useRealtimeRefresh } from '../../hooks/useRealtimeRefresh';
import { useSiteSettings } from '../providers/RealtimeSiteSettingsProvider';
import { PageWidgetsRenderer } from './widgets/PageWidgetsRenderer';
import type { PageWidget } from '../../types/content';
import { STORAGE_ASSET_URLS } from '../../lib/content/storageAssets';

const homeFamilyImages = {
  hero: STORAGE_ASSET_URLS.familyImage1,
};

const accordionTitleStyle = {
  color: 'var(--text-primary)',
  fontWeight: 700,
  fontSize: 'clamp(1.02rem, 1.2vw, 1.18rem)',
  letterSpacing: '-0.01em',
  lineHeight: 1.45,
} as const;

const homeCardSurfaceCycle = [
  'var(--surface-base)',
  'var(--surface-soft)',
  'var(--surface-muted)',
  'var(--surface-alt)',
] as const;

export function HomeView({
  data: initialData,
  locale,
  widgets,
}: {
  data: HomepageData;
  locale: Locale;
  widgets?: PageWidget[];
}) {
  const siteSettings = useSiteSettings();
  const offersScrollerRef = useRef<HTMLDivElement | null>(null);
  const [offersCanScrollLeft, setOffersCanScrollLeft] = useState(false);
  const [offersCanScrollRight, setOffersCanScrollRight] = useState(false);

  const data = useRealtimeContent<HomepageData>(
    initialData,
    locale,
    [
      'homepage_content',
      'homepage_highlights',
      'homepage_stats',
      'homepage_featured_pages',
      'homepage_steps',
      'homepage_faqs',
      'homepage_testimonials',
    ],
    (prev, table, rows) => {
      switch (table) {
        case 'homepage_content': {
          const row = rows[0];
          if (!row) return prev;
          return {
            ...prev,
            heroHeading: row.title ?? prev.heroHeading,
            heroBody: row.body ?? prev.heroBody,
            primaryCtaLabel: row.primary_cta_label ?? prev.primaryCtaLabel,
            primaryCtaHref: row.href ?? prev.primaryCtaHref,
            secondaryCtaLabel: row.secondary_cta_label ?? prev.secondaryCtaLabel,
            secondaryCtaHref: row.secondary_cta_href ?? prev.secondaryCtaHref,
            heroImageUrl: row.hero_image_url ?? prev.heroImageUrl,
            heroImageAlt: row.hero_image_alt ?? prev.heroImageAlt,
            trustLabel: row.trust_label ?? prev.trustLabel,
            previewLabel: row.preview_label ?? prev.previewLabel,
            previewImageUrl: row.preview_image_url ?? prev.previewImageUrl,
            featureIntroLabel: row.feature_intro_label ?? prev.featureIntroLabel,
            featureIntroHeading: row.feature_intro_heading ?? prev.featureIntroHeading,
          };
        }
        case 'homepage_highlights':
          return {
            ...prev,
            highlights: rows.map((r) => ({
              eyebrow: r.eyebrow || '',
              title: r.title || '',
              body: r.body || '',
              href: r.href || '',
            })),
          };
        case 'homepage_stats':
          return {
            ...prev,
            stats: rows.map((r) => ({
              value: r.value || '',
              label: r.label || '',
              description: r.description,
            })),
          };
        case 'homepage_featured_pages':
          return {
            ...prev,
            featuredPages: rows.map((r) => ({
              title: r.title || '',
              body: r.body || '',
              href: r.href || '',
              imageUrl: r.image_url,
              imageAlt: r.image_alt,
            })),
          };
        case 'homepage_steps':
          return {
            ...prev,
            steps: rows.map((r) => ({
              title: r.title || '',
              body: r.body || '',
            })),
          };
        case 'homepage_faqs':
          return {
            ...prev,
            faqs: rows.map((r) => ({
              question: r.question || '',
              answer: r.answer || '',
            })),
          };
        case 'homepage_testimonials':
          return {
            ...prev,
            testimonials: rows.map((r) => ({
              name: r.name || '',
              quote: r.quote || '',
            })),
          };
        default:
          return prev;
      }
    },
  );

  const refreshSubscriptions = useMemo(
    () => [
      { table: 'homepage_content', filter: `locale=eq.${locale}` },
      { table: 'homepage_highlights', filter: `locale=eq.${locale}` },
      { table: 'homepage_stats', filter: `locale=eq.${locale}` },
      { table: 'homepage_featured_pages', filter: `locale=eq.${locale}` },
      { table: 'homepage_steps', filter: `locale=eq.${locale}` },
      { table: 'homepage_faqs', filter: `locale=eq.${locale}` },
      { table: 'homepage_testimonials', filter: `locale=eq.${locale}` },
      { table: 'page_widgets', filter: 'page_key=eq.home' },
    ],
    [locale],
  );
  useRealtimeRefresh(refreshSubscriptions);

  const stats = (data.stats || []).map((stat) => ({
    value: stat.value,
    label: stat.label,
    description: stat.description,
  }));

  const featuredPages = (data.featuredPages || []).map((page) => ({
    ...page,
    body: stripHtmlToPlainText(page.body),
    href: resolvePublicHref(locale, page.href || ''),
  }));
  const highlights = (data.highlights || [])
    .filter((item) => item.title || item.body)
    .map((item) => ({
      ...item,
      body: stripHtmlToPlainText(item.body),
      href: item.href ? resolvePublicHref(locale, item.href) : undefined,
    }));

  const homeFaqs = (data.faqs || []).filter((item) => item.question || item.answer);
  const testimonials = (data.testimonials || []).filter((item) => item.name || item.quote);
  const resolvedWidgets = widgets || [];
  const primaryCtaHref = data.primaryCtaHref
    ? resolvePublicHref(locale, data.primaryCtaHref, { includeLegacyAutovakuutus: true })
    : undefined;

  useEffect(() => {
    const scroller = offersScrollerRef.current;
    if (!scroller) return;

    const updateScrollState = () => {
      const maxScrollLeft = scroller.scrollWidth - scroller.clientWidth;
      setOffersCanScrollLeft(scroller.scrollLeft > 4);
      setOffersCanScrollRight(scroller.scrollLeft < maxScrollLeft - 4);
    };

    updateScrollState();
    scroller.addEventListener('scroll', updateScrollState, { passive: true });
    window.addEventListener('resize', updateScrollState);

    return () => {
      scroller.removeEventListener('scroll', updateScrollState);
      window.removeEventListener('resize', updateScrollState);
    };
  }, [featuredPages.length]);

  const scrollOffers = (direction: 1 | -1) => {
    const scroller = offersScrollerRef.current;
    if (!scroller) return;
    scroller.scrollBy({
      left: direction * Math.max(scroller.clientWidth * 0.72, 260),
      behavior: 'smooth',
    });
  };

  return (
    <PublicPageShell>
      <Header locale={locale} settings={siteSettings} />

      <HeroBlock
        title={data.heroHeading}
        subtitle={data.heroBody}
        primaryCtaLabel={
          data.primaryCtaLabel
        }
        primaryCtaHref={primaryCtaHref}
        secondaryCtaLabel={data.secondaryCtaLabel}
        secondaryCtaHref={data.secondaryCtaHref ? resolvePublicHref(locale, data.secondaryCtaHref) : undefined}
        backgroundImage={homeFamilyImages.hero}
        locale={locale}
        variant="home"
      />

      {highlights.length ? (
        <Container size="xl" pb={{ base: 34, md: 46 }}>
          <Stack gap={16}>
            <SimpleGrid cols={{ base: 1, md: 3 }} spacing={14}>
              {highlights.map((item, index) => (
                <Paper
                  key={`${item.title}-${index}`}
                  p={20}
                  radius={20}
                  withBorder
                  style={{
                    borderColor: 'var(--surface-border)',
                    borderWidth: 1.5,
                    background: homeCardSurfaceCycle[index % homeCardSurfaceCycle.length],
                  }}
                >
                  <Stack gap={8}>
                    {item.eyebrow ? (
                      <Text size="xs" fw={700} tt="uppercase" style={{ letterSpacing: '0.04em', color: 'var(--text-muted)' }}>
                        {item.eyebrow}
                      </Text>
                    ) : null}
                    <Text fw={600} style={{ color: 'var(--text-primary)' }}>
                      {item.title}
                    </Text>
                    <Text size="sm" style={{ color: 'var(--text-secondary)', lineHeight: 1.7 }}>
                      {item.body}
                    </Text>
                    {item.href ? (
                      <Button
                        component={Link}
                        href={item.href}
                        {...getCarComparisonLinkProps(item.href)}
                        variant="default"
                        radius="xl"
                        size="xs"
                        rightSection={<IconArrowRight size={14} />}
                        styles={{
                          root: {
                            width: 'fit-content',
                            borderColor: 'var(--surface-border)',
                            background: index % 2 === 0 ? 'var(--surface-base)' : 'var(--surface-soft)',
                            color: 'var(--text-primary)',
                            fontWeight: 600,
                          },
                        }}
                      >
                        {pickLocaleString({ fi: 'Lue lisää', sv: 'Läs mer', en: 'Learn more' }, locale)}
                      </Button>
                    ) : null}
                  </Stack>
                </Paper>
              ))}
            </SimpleGrid>
          </Stack>
        </Container>
      ) : null}

      {featuredPages.length ? (
        <Container size="xl" py={{ base: 34, md: 52 }}>
          <Stack gap={20}>
            <Group justify="space-between" align="end" wrap="nowrap">
              <Box maw={780}>
                {data.featureIntroHeading ? (
                  <Title
                    order={2}
                    style={{ color: 'var(--text-primary)', fontWeight: 600, lineHeight: 1.35, letterSpacing: '-0.01em', fontSize: 'clamp(1.95rem, 3.6vw, 2.45rem)' }}
                  >
                    {data.featureIntroHeading}
                  </Title>
                ) : null}
                {data.trustLabel ? (
                  <Text mt={10} style={{ color: 'var(--text-secondary)', lineHeight: 1.75, maxWidth: 760 }}>
                    {data.trustLabel}
                  </Text>
                ) : null}
              </Box>

              <Group gap={8} wrap="nowrap">
                <Button
                  aria-label={pickLocaleString({ fi: 'Edelliset kortit', sv: 'Föregående kort', en: 'Previous cards' }, locale)}
                  variant="default"
                  radius="xl"
                  disabled={!offersCanScrollLeft}
                  onClick={() => scrollOffers(-1)}
                  styles={{
                    root: {
                      borderColor: 'var(--surface-border)',
                      background: 'var(--surface-base)',
                      color: offersCanScrollLeft ? 'var(--text-primary)' : 'var(--text-muted)',
                      opacity: offersCanScrollLeft ? 1 : 0.55,
                      cursor: offersCanScrollLeft ? 'pointer' : 'not-allowed',
                    },
                  }}
                >
                  <IconChevronLeft size={16} />
                </Button>
                <Button
                  aria-label={pickLocaleString({ fi: 'Seuraavat kortit', sv: 'Nästa kort', en: 'Next cards' }, locale)}
                  variant={offersCanScrollRight ? 'filled' : 'default'}
                  radius="xl"
                  disabled={!offersCanScrollRight}
                  onClick={() => scrollOffers(1)}
                  styles={{
                    root: {
                      borderColor: offersCanScrollRight ? 'transparent' : 'var(--surface-border)',
                      background: offersCanScrollRight ? 'var(--brand-primary)' : 'var(--surface-base)',
                      color: offersCanScrollRight ? '#fff' : 'var(--text-muted)',
                      opacity: offersCanScrollRight ? 1 : 0.55,
                      cursor: offersCanScrollRight ? 'pointer' : 'not-allowed',
                    },
                  }}
                >
                  <IconChevronRight size={16} />
                </Button>
              </Group>
            </Group>

            <Box
              ref={offersScrollerRef}
              style={{
                display: 'grid',
                gridAutoFlow: 'column',
                gridAutoColumns: 'minmax(min(82vw, 260px), calc((100% - 60px) / 4))',
                gap: 20,
                overflowX: 'auto',
                scrollSnapType: 'x mandatory',
                paddingBottom: 8,
              }}
            >
              {featuredPages.map((item, index) => (
                <Paper
                  key={`${item.href}-${index}`}
                  p={22}
                  radius={20}
                  withBorder
                  style={{
                    borderColor: 'var(--surface-border)',
                    borderWidth: 1.5,
                    background: homeCardSurfaceCycle[(index + 1) % homeCardSurfaceCycle.length],
                    minHeight: 192,
                    scrollSnapAlign: 'start',
                  }}
                >
                  <Stack gap={10} justify="space-between" h="100%">
                    <Stack gap={8}>
                      <Text fw={600} style={{ color: 'var(--text-primary)' }}>
                        {item.title}
                      </Text>
                      <Text size="sm" style={{ color: 'var(--text-secondary)', lineHeight: 1.65 }}>
                        {item.body}
                      </Text>
                    </Stack>
                    <Button
                      component={Link}
                      href={item.href}
                      {...getCarComparisonLinkProps(item.href)}
                      variant="default"
                      radius="xl"
                      size="xs"
                      styles={{
                        root: {
                          width: 'fit-content',
                          borderColor: 'var(--surface-border)',
                          background: index % 2 === 0 ? 'var(--surface-base)' : 'var(--surface-muted)',
                          color: 'var(--text-primary)',
                          fontWeight: 600,
                        },
                      }}
                    >
                      {pickLocaleString({ fi: 'Lue lisää', sv: 'Läs mer', en: 'Learn more' }, locale)}
                    </Button>
                  </Stack>
                </Paper>
              ))}
            </Box>
          </Stack>
        </Container>
      ) : null}

      {homeFaqs.length ? (
        <Container size="xl" pb={{ base: 30, md: 60 }}>
          <SimpleGrid cols={{ base: 1 }} spacing={20}>
            <Paper p={16} radius={40} withBorder style={{ borderColor: 'var(--surface-border)', background: 'var(--surface-base)' }}>
              <Accordion variant="separated" radius="xl" defaultValue={homeFaqs[0] ? 'f-0' : null}>
                {homeFaqs.slice(0, 5).map((item, index) => (
                  <Accordion.Item
                    key={`${item.question}-${index}`}
                    value={`f-${index}`}
                    style={{ borderColor: 'var(--surface-border)', background: index === 0 ? 'var(--surface-soft)' : 'var(--surface-base)' }}
                  >
                    <Accordion.Control style={accordionTitleStyle}>
                      {item.question || `FAQ ${index + 1}`}
                    </Accordion.Control>
                    <Accordion.Panel>
                      <Text style={{ color: 'var(--text-secondary)', lineHeight: 1.75 }}>{item.answer}</Text>
                    </Accordion.Panel>
                  </Accordion.Item>
                ))}
              </Accordion>
            </Paper>
          </SimpleGrid>
        </Container>
      ) : null}

      {testimonials.length ? (
        <Container size="xl" pb={{ base: 30, md: 60 }}>
          <Stack gap={16}>
            <SimpleGrid cols={{ base: 1, md: 2 }} spacing={16}>
              {testimonials.map((item, index) => (
                <Paper
                  key={`${item.name}-${index}`}
                  p={22}
                  radius={22}
                  withBorder
                  style={{
                    borderColor: 'var(--surface-border)',
                    background: index % 2 === 0 ? 'var(--surface-base)' : 'var(--surface-soft)',
                  }}
                >
                  <Stack gap={10}>
                    <Text style={{ color: 'var(--text-secondary)', lineHeight: 1.75 }}>&ldquo;{item.quote}&rdquo;</Text>
                    <Text fw={600} style={{ color: 'var(--text-primary)' }}>
                      {item.name}
                    </Text>
                  </Stack>
                </Paper>
              ))}
            </SimpleGrid>
          </Stack>
        </Container>
      ) : null}

      <StatsGridBlock
        title={undefined}
        items={stats.length ? stats : []}
      />

      <PageWidgetsRenderer locale={locale} widgets={resolvedWidgets} />

      <Footer locale={locale} settings={siteSettings} />
    </PublicPageShell>
  );
}
